import { CompliancePageListDueDiligenceComplianceRecordsQuery } from '@/__generated__/graphql';
import { ISelectValue } from '@/components/basicComponents/select';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { Loader } from '@/components/loaders/loader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { SORT_ORDER } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { dueDiligenceHistoryMobileTableSort, dueDiligenceHistoryTableColumns } from '../constants';
import { LIST_DUE_DILIGENCE_COMPLIANCE_RECORDS_QUERY } from '../queries';
import { DueDiligenceHistoryTable } from './table/table';
import DueDiligenceHistoryMobileTable from './table/tableMobile';

const statusFilterOptions: ISelectValue[] = [
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' }
];

export const DueDiligenceHistory = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const { searchParams, onChangeSort, onChangeSearch, onChangePrimaryFilter, onChangePaginationData, makeScroll, saveScrollPostion } =
    useSearchParamsManager();

  const backToTitle = searchParams.get('backToTitle') || '';
  const q = searchParams.get('q') || '';
  const quickFilter = searchParams.get('quickFilter') || '';
  const key = searchParams.get('key');
  const asc = searchParams.get('asc') === 'true' || false;
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const position = searchParams.get('position') || 0;

  const [dueDiligenceTableRows, setDueDiligenceTableRows] = useState<
    CompliancePageListDueDiligenceComplianceRecordsQuery['listDueDiligenceComplianceRecords']['records']
  >([]);
  const [mobileSort, setMobileSort] = useState(dueDiligenceHistoryMobileTableSort[10].title);
  const sortItem = dueDiligenceHistoryMobileTableSort.find((item) => item.title === mobileSort);
  const mobileSortList = dueDiligenceHistoryMobileTableSort.map((item) => item.title);

  const {
    data: dueDiligenceData,
    loading: dueDiligenceLoading,
    refetch: dueDiligenceRefetch
  } = useQuery<{ listDueDiligenceComplianceRecords: CompliancePageListDueDiligenceComplianceRecordsQuery['listDueDiligenceComplianceRecords'] }>(
    LIST_DUE_DILIGENCE_COMPLIANCE_RECORDS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        data: {
          search: q,
          sortBy: key || 'statusUpdatedAt',
          sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
          statusFilter: !quickFilter ? ['Approved', 'Rejected'] : quickFilter || '',
          offset: Number(offset),
          limit: Number(limit)
        }
      },
      onCompleted: (data) => {
        setDueDiligenceTableRows(data.listDueDiligenceComplianceRecords.records);
        makeScroll(position);
      }
    }
  );

  useEffect(() => {
    if (!isMobile) return;

    dueDiligenceRefetch({
      data: {
        search: q,
        sortBy: sortItem?.key,
        sortDirection: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        statusFilter: !quickFilter ? ['Approved', 'Rejected'] : quickFilter || '',
        offset: Number(offset),
        limit: Number(limit)
      }
    });
  }, [mobileSort]);

  const openPage = (path: string) => {
    saveScrollPostion();
    navigate(path);
  };

  return (
    <>
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/compliance' })} backToTitle={backToTitle} />} />
        <PageTitle title="Due Diligence History" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search for an investment'
            }}
            quickFilter={{
              title: 'Status',
              value: quickFilter,
              defaultValue: 'All',
              options: [{ label: 'All', value: '' }, ...statusFilterOptions],
              onChange: (value) => onChangePrimaryFilter(value, 'quickFilter')
            }}
            resultsValue={dueDiligenceData?.listDueDiligenceComplianceRecords?.total || 0}
            refetch={dueDiligenceRefetch}
            mobileSort={{ value: mobileSort, onChange: setMobileSort, selects: mobileSortList }}
            useNewFilters
          />
          {isMobile ? (
            <>
              {dueDiligenceLoading ? (
                <Loader />
              ) : (
                <DueDiligenceHistoryMobileTable
                  tableColumns={dueDiligenceHistoryTableColumns}
                  tableRows={dueDiligenceTableRows}
                  openPage={openPage}
                />
              )}
            </>
          ) : (
            <DueDiligenceHistoryTable
              tableColumns={dueDiligenceHistoryTableColumns}
              tableRows={dueDiligenceTableRows}
              sort={{ key, asc }}
              loading={dueDiligenceLoading}
              setSort={onChangeSort}
              openPage={openPage}
            />
          )}
          <PaginationWrap>
            <TablePagination
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: dueDiligenceData?.listDueDiligenceComplianceRecords?.total ?? 0
              }}
              savePagination={onChangePaginationData}
              refetch={() => {}}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
